<template lang="pug">
  campaign-layout
    v-container(dark fluid grid-list-xl)
      v-layout(column)
        v-flex
          router-link.mb-3(tag="button" :to="{ name: 'published-campaigns' }")
            v-icon(small) keyboard_arrow_left
            | All Published Campaigns
          action-title-bar(
            :title="campaignName"
            :description="totalItemsText"
          )
      v-layout(row)
        v-flex
          v-card
            verify-access-code(
              :published-campaign="publishedCampaign"
              :query-params="queryParams"
              :is-visible="requiresAccessCode"
            )
            v-data-table(
              :headers="headers"
              :items="actions"
              :class="{ 'in-progress': isLoading }"
              @update:pagination="sortResults"
              item-key="id"
              hide-actions
            )
              template(slot="items" slot-scope="data")
                published-campaign-preview-item(:action="data.item" data-cy="campaign-action")

            v-layout.pagination
               v-flex.pages
                 v-pagination(
                   v-if="paginationPageCount(total, pageSize) > 1"
                   v-model="paginationPage"
                   :length="paginationPageCount(total, pageSize)"
                   :total-visible="7"
                 )
               v-flex.limits
                 pagination-limit.mr-4(
                   :limits="paginationLimits"
                   :current-limit="pageSize"
                   @change:limit="loadWithNewPageLimit($event, 'publishedCampaignActionsPageSize')"
                 )
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Pagination from '@/Core/mixins/pagination';
import ShowItemTotals from '@/Core/mixins/show-item-totals';
import PaginationLimit from '@/Core/components/PaginationLimit';
import CampaignLayout from '@/Campaigns/Layout';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import VerifyAccessCode from '@/Campaigns/components/VerifyAccessCode';
import PublishedCampaignPreviewItem from './PublishedCampaignPreviewItem';

export default {
  components: {
    CampaignLayout,
    PublishedCampaignPreviewItem,
    ActionTitleBar,
    VerifyAccessCode,
    PaginationLimit,
  },
  mixins: [Pagination, ShowItemTotals],
  mounted() {
    const { id } = this.$route.params;
    this.getOnePublishedCampaign(id);
    if (!this.requiresAccessCode) {
      this.findPublishedCampaignActions(this.queryParamObject);
    }
  },
  data() {
    return {
      sortParams: {
        descending: false,
        sortBy: 'action_type_id', // aka action type
      },
    };
  },
  computed: {
    ...mapGetters('publishedCampaigns', ['headers', 'specialSorts']),
    ...mapState({
      pageSize: state => state.preferences.publishedCampaignActionsPageSize,
      publishedCampaign: state => state.publishedCampaigns.campaign,
      actions: state => state.publishedCampaigns.actions,
      isLoading: state => state.publishedCampaigns.isLoading,
      total: state => state.publishedCampaigns.totalActions,
    }),
    totalItemsText() {
      return this.showItemTotals_show(
        0,
        this.total,
        'Campaign Action',
        'Campaign Actions',
      );
    },
    requiresAccessCode() {
      const { requiresAccessCode, accessCode } = this.$route.params;
      /**
       * if redirecting from the published-campaigns page, the requiresAccessCode
       * route param exists
       */
      if (typeof requiresAccessCode === 'boolean') {
        return requiresAccessCode && !accessCode;
      }
      /**
       * if not redirected from the published campaigns page, then need to
       * query the API to check if access code is needed
       */
      return this.publishedCampaign.requires_access_code;
    },
    previewParams() {
      const { id, requiresAccessCode, accessCode } = this.$route.params;

      if (requiresAccessCode) return { id, accessCode };
      return { id };
    },
    queryParams() {
      return {
        page: this.paginationPage,
        descending: this.sortParams.descending,
        sortByField: this.sortParams.sortBy || 'action_type_id',
        limit: this.pageSize,
      };
    },
    queryParamObject() {
      return {
        ...this.previewParams,
        ...this.queryParams,
      };
    },
    campaignName() {
      const { campaign } = this.publishedCampaign;
      if (campaign) return campaign.name;
    },
  },
  watch: {
    paginationPage(value) {
      this.findPublishedCampaignActions({
        ...this.queryParamObject,
        page: value,
      });
    },
    pageSize(value) {
      this.findPublishedCampaignActions(this.queryParamObject);
    },
  },
  methods: {
    ...mapActions('publishedCampaigns', [
      'findPublishedCampaignActions',
      'getOnePublishedCampaign',
    ]),
    sortResults(value) {
      if (value.sortBy) {
        this.sortParams = Object.assign({}, value);

        if (this.isSortingOnMultipleFields(this.specialSorts, value.sortBy)) {
          const sortFields = this.specialSorts[value.sortBy];
          this.sortParams = { sortBy: sortFields, descending: value.descending };
        }
      }

      this.findPublishedCampaignActions(this.queryParamObject);
    },
  },
};
</script>
